import {
  CompetitionStageDto,
  ResultNoticeDuty,
  SourcingEventStatus,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import { isDesignContest } from "../../sourcing-events/buyer/wizard/utils/designContest";
import { autoKarensDays } from "../../sourcing-events/buyer/wizard/utils/karens";
import { isSchemeProcedure, isTwoStageProcedure } from "../../sourcing-events/buyer/wizard/utils/procedure";
import type { MilestoneKey } from "../../sourcing-events/buyer/wizard/utils/milestones";
import { isWithoutCompetition } from "../../sourcing-events/buyer/wizard/utils/noCompetition";

export type TimelinePhaseKey = MilestoneKey | "requestDeadline" | "qualification" | "resultNotice";

export interface TimelinePhase {
  visible: boolean;
  idle: boolean;
  activeKey: TimelinePhaseKey | null;
  completedKeys: TimelinePhaseKey[];
}

const FULL_PHASE_ORDER: readonly TimelinePhaseKey[] = [
  "announcement",
  "bidDeadline",
  "evaluationStart",
  "awardDecision",
  "standstillExpiry",
  "contractSigning",
];

const TWO_STAGE_PHASE_ORDER: readonly TimelinePhaseKey[] = [
  "announcement",
  "requestDeadline",
  "qualification",
  "bidDeadline",
  "evaluationStart",
  "awardDecision",
  "standstillExpiry",
  "contractSigning",
];

// An ordning is announced and then takes applications for the rest of its life (FOA § 26-6). It
// has no tender phase, no evaluation and no award of its own — those belong to each purchase
// under it — so its run really is these two steps.
const DPS_SCHEME_PHASE_ORDER: readonly TimelinePhaseKey[] = ["announcement", "requestDeadline"];

const WITHOUT_COMPETITION_PHASE_ORDER: readonly TimelinePhaseKey[] = ["awardDecision", "contractSigning"];

const publicProcedureOf = (event: SourcingEventDto) =>
  event.eventContext.type === "Public" ? event.eventContext.procedureType : null;

export const phasesFor = (event: SourcingEventDto): TimelinePhaseKey[] => {
  const phasesPublicCtx = event.eventContext.type === "Public" ? event.eventContext : null;
  const procedureType = publicProcedureOf(event);
  if (isSchemeProcedure(procedureType)) return [...DPS_SCHEME_PHASE_ORDER];
  const mustAnnounceResult = event.award?.resultNotice?.duty === ResultNoticeDuty.Required;
  if (isWithoutCompetition(procedureType)) {
    return [...WITHOUT_COMPETITION_PHASE_ORDER, ...(mustAnnounceResult ? ["resultNotice" as const] : [])];
  }
  const hasStandstill =
    autoKarensDays(phasesPublicCtx?.regulation ?? null, phasesPublicCtx?.foaPart ?? null, procedureType) != null;
  const order = isTwoStageProcedure(procedureType) ? TWO_STAGE_PHASE_ORDER : FULL_PHASE_ORDER;
  const signsContract = !isDesignContest(procedureType);
  return [...order, ...(mustAnnounceResult ? ["resultNotice" as const] : [])].filter(
    (key) => (key !== "standstillExpiry" || hasStandstill) && (key !== "contractSigning" || signsContract),
  );
};

const HIDDEN_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([SourcingEventStatus.Draft]);

const IDLE_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Cancelled,
  SourcingEventStatus.PublicationPending,
  SourcingEventStatus.PublicationFailed,
]);

const HIDDEN: TimelinePhase = { visible: false, idle: false, activeKey: null, completedKeys: [] };

const hasPassed = (iso: string | null | undefined, now: number): boolean =>
  iso != null && now >= Date.parse(iso);

export const deriveTimelinePhase = (event: SourcingEventDto, now: number): TimelinePhase => {
  const { status } = event;
  if (HIDDEN_STATUSES.has(status)) return HIDDEN;

  const phases = phasesFor(event);

  if (IDLE_STATUSES.has(status)) {
    return { visible: true, idle: true, activeKey: null, completedKeys: [] };
  }
  if (status === SourcingEventStatus.Closed || status === SourcingEventStatus.Completed) {
    return { visible: true, idle: false, activeKey: null, completedKeys: [...phases] };
  }
  // No stage left once the validity runs out, and no further step is coming.
  if (isSchemeProcedure(publicProcedureOf(event)) && event.competitionStage == null) {
    return { visible: true, idle: false, activeKey: null, completedKeys: [...phases] };
  }

  const activeKey = activeKeyFor(event, now, phases);
  if (activeKey == null) return HIDDEN;

  const activeIndex = phases.indexOf(activeKey);
  const bidDeadline = event.milestoneDates?.bidSubmissionDeadline;
  const stillTakingBids = bidDeadline != null && !hasPassed(bidDeadline, now);
  const completedKeys = phases
    .slice(0, activeIndex)
    .filter((key) => key !== "bidDeadline" || !stillTakingBids);
  return { visible: true, idle: false, activeKey, completedKeys };
};

const activeKeyFor = (
  event: SourcingEventDto,
  now: number,
  phases: readonly TimelinePhaseKey[],
): TimelinePhaseKey | null => {
  switch (event.status) {
    case SourcingEventStatus.Published:
      if (isTwoStageProcedure(event.eventContext.type === "Public" ? event.eventContext.procedureType : undefined)) {
        switch (event.competitionStage) {
          case CompetitionStageDto.InterestOpen:
            return "announcement";
          case CompetitionStageDto.RequestsOpen:
            return "requestDeadline";
          case CompetitionStageDto.Qualification:
            return "qualification";
          case CompetitionStageDto.Tendering:
            return "bidDeadline";
          default:
            // Legacy two-stage data without a round behaves single-stage.
            return hasPassed(event.milestoneDates?.bidSubmissionDeadline, now) ? "bidDeadline" : "announcement";
        }
      }
      return hasPassed(event.milestoneDates?.bidSubmissionDeadline, now) ? "bidDeadline" : "announcement";
    case SourcingEventStatus.Evaluation:
      return "evaluationStart";
    case SourcingEventStatus.Evaluated:
      return "awardDecision";
    case SourcingEventStatus.AwardDecision:
      // Deliberately no hasPassed (unlike Awarded below): confirmation is still pending after expiry.
      return phases.includes("standstillExpiry") && event.award?.standstillExpiresAt != null
        ? "standstillExpiry"
        : "awardDecision";
    case SourcingEventStatus.Awarded:
      if (phases.includes("resultNotice") && event.award?.contractConcludedOn != null) return "resultNotice";
      if (!phases.includes("standstillExpiry")) return "contractSigning";
      return hasPassed(event.award?.standstillExpiresAt, now) ? "contractSigning" : "standstillExpiry";
    default:
      return null;
  }
};
